import { yupResolver } from '@hookform/resolvers/yup'
import React, { FunctionComponent } from 'react'
import { Col } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { ClinTheme } from '../../../ClinTheme'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinTextInput } from '../../../components/ClinTextInput'
import {
  StyledRowTitle,
  StyledClinSecondaryButton,
  StyledClinPrimaryButton,
  StyledLoadingContainer
} from './AccountDetails.styles'
import { StyledLoadingSpinner } from '../../Onboarding/Onboarding.styles'
import { ClinText } from '../../../components/ClinText'

export enum FormSubmitState {
  Form,
  Loading,
  Success,
  Error
}

type ChangePasswordFormData = {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

interface IChangePasswordProps {
  /** State of the form */
  formState?: FormSubmitState
  /** New password callback when form submitted */
  handleSubmitClick?: (
    password: string,
    newPassword: string,
    newPasswordConfirmation: string
  ) => void
  handleCancelClick?: () => void
}

export const AccountDetailsChangePassword: FunctionComponent<
  IChangePasswordProps
> = ({
  formState = FormSubmitState.Form,
  handleSubmitClick,
  handleCancelClick
}) => {
  const { t } = useTranslation()

  const changePasswordSchema = yup.object().shape({
    currentPassword: yup
      .string()
      .required(t('account_details:change_password.password_current')),
    newPassword: yup
      .string()
      .required(t('account_details:change_password.password_required'))
      .min(10, t('account_details:change_password.password_minimum_chars'))
      .matches(
        /^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[*.#!@$%^&(){}[\]:;<>,?/~_+\-=|]).{10,}$/,
        t('account_details:change_password.password_minimum_requirements')
      ),
    newPasswordConfirmation: yup
      .string()
      .oneOf(
        [yup.ref('newPassword'), undefined],
        t('account_details:change_password.password_mismatch')
      )
  })

  const {
    handleSubmit,
    formState: { errors },
    control
  } = useForm<ChangePasswordFormData>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirmation: ''
    },
    resolver: yupResolver(changePasswordSchema)
  })

  const onSubmit = handleSubmit(
    ({
      currentPassword,
      newPassword,
      newPasswordConfirmation
    }: ChangePasswordFormData) => {
      if (newPassword === newPasswordConfirmation) {
        handleSubmitClick &&
          handleSubmitClick(
            currentPassword,
            newPassword,
            newPasswordConfirmation
          )
      }
    }
  )

  return (
    <>
      <Col xs={12}>
        <Controller
          name="currentPassword"
          control={control}
          render={({ field }) => (
            <>
              <StyledRowTitle isEdit>
                {t('account_details:change_password:title')}
              </StyledRowTitle>
              <ClinTextInput
                {...field}
                id="current-password"
                type="password"
                hasError={!!errors.currentPassword}
                prompt={errors.currentPassword?.message}
              />
            </>
          )}
        />
        <ClinSpacer />
      </Col>
      <Col xs={12}>
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <>
              <StyledRowTitle isEdit>
                {t('account_details:change_password:new_password')}
              </StyledRowTitle>
              <ClinTextInput
                {...field}
                id="new-password"
                type="password"
                hasError={!!errors.newPassword}
                prompt={errors.newPassword?.message}
              />
            </>
          )}
        />
        <ClinText
          marginTop={ClinTheme.space[2]}
          fontSize={ClinTheme.fontSizes[0]}
          lineHeight={ClinTheme.lineHeights.heading[1]}
        >
          {t('account_details:change_password:validation_message')}
        </ClinText>
        <ClinSpacer />
      </Col>
      <Col xs={12}>
        <Controller
          name="newPasswordConfirmation"
          control={control}
          render={({ field }) => (
            <>
              <StyledRowTitle isEdit>
                {t('account_details:change_password:password_confirmation')}
              </StyledRowTitle>
              <ClinTextInput
                {...field}
                id="retype-password"
                type="password"
                hasError={!!errors.newPasswordConfirmation}
                prompt={errors.newPasswordConfirmation?.message}
              />
            </>
          )}
        />
        <ClinSpacer height={ClinTheme.space[3]} />
      </Col>
      <Col xs={12}>
        <ClinGroup>
          <StyledClinSecondaryButton onClick={handleCancelClick}>
            {t('common:labels.cancel')}
          </StyledClinSecondaryButton>
          <StyledLoadingContainer>
            <StyledClinPrimaryButton
              variant="primary"
              disabled={formState === FormSubmitState.Loading}
              onClick={() => {
                void onSubmit()
              }}
            >
              {t('common:labels.save')}
            </StyledClinPrimaryButton>
            {formState === FormSubmitState.Loading && (
              <StyledLoadingSpinner>
                <ClinSpinner />
              </StyledLoadingSpinner>
            )}
          </StyledLoadingContainer>
        </ClinGroup>
      </Col>
    </>
  )
}
